<template>
  <v-list-group
    :append-icon="showTextMessage ? 'mdi-menu-down' : null"
    :disabled="!showTextMessage"
    :ripple="false"
  >
    <template v-slot:activator>
      <v-list-item dense>
        <v-list-item-content class="flex-nowrap">
          <v-list-item-icon class="align-self-center ma-0 flex-shrink-1">
            <v-icon>{{ actionIcon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content :class="titleClass" class="mr-2 title-box">
            <v-list-item-title class="text-wrap">
              {{ data.title || '' }} <span class="font-weight-bold">({{ status }})</span>
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ subtitle }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action class="flex-shrink-1 justify-center ma-0">
            <v-tooltip v-if="copyableDownloadUrl" left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="copyDownloadLink"
                >
                  <v-icon size="18" color="defaultIconColor">mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('copy_download_link') }}</span>
            </v-tooltip>

            <v-btn
              v-if="data.status == 'in_progress' && data.cancelSource"
              icon
              @click.stop="cancel"
            >
              <v-icon size="18" color="defaultIconColor">mdi-close-circle</v-icon>
            </v-btn>

            <v-icon
              v-else
              size="18"
              :color="statusIcon.color"
            >{{ statusIcon.icon }}</v-icon>
          </v-list-item-action>
        </v-list-item-content>
      </v-list-item>
    </template>

    <v-list-item>
      <v-list-item-subtitle class="text-wrap ma-4 mt-0 ml-7 pl-6">
        {{ data.text }}
      </v-list-item-subtitle>
    </v-list-item>
  </v-list-group>
</template>

<style scoped>
  .word-break {
    word-break: break-all;
    overflow-wrap: break-word;
    text-wrap: wrap;
    hyphens: auto;
  }

  .title-box {
    max-width: 70%;
  }

  .v-list-item {
    padding: 0;
  }

  *:hover {
    background-color: transparent !important;
  }

  ::v-deep .v-list-group__header__append-icon {
    margin: 0 !important;
    min-width: 24px !important;
    padding-right: 6px !important;
  }
  
  ::v-deep .v-list-group__header{
    padding-right: 0 !important;
  }

  ::v-deep .v-list-item:hover::before {
    opacity: 0 !important;
  }
</style>

<script>

import { mapActions } from 'vuex'

import BackgroundActions from '@/store/background_actions/actions-types.js'
import HumanReadable from "@/utils/human_readable";

export default {
  name: 'BackgroundActionItem',

  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    status() {
      if (this.data.status != 'in_progress') {
        return this.$t(this.data.status)
      }

      return `${this.data.progress}%`
    },

    subtitle() {
      return HumanReadable.epochTimestampToDate(this.data.createdAt, true);
    },

    actionIcon() {

      if(this.data.icon) {
        return this.data.icon
      }

      switch (this.data.type) {
        case 'upload': return 'mdi-file-upload-outline'
        case 'move': return 'mdi-folder-move-outline'
        case 'copy': return 'mdi-content-copy'
        case 'delete': return 'mdi-delete-outline'
        case 'customAction': return 'mdi-motion-play-outline'
        case 'export': return 'mdi-image-multiple-outline'
        case 'synthetic_image_txt2img': return 'mdi-creation-outline'
        case 'synthetic_image_upscale': return 'mdi-resize'
        default: return 'mdi-reload';
      }
    },

    titleClass() {
      const words = this.data.title?.split(/\s+/);
      if(words && words.length > 0) {
        const hasLongWord = words.some(word => word.length > 24);
        return hasLongWord ? 'word-break' : 'text-wrap';
      }
      return 'text-wrap';
    },

    statusIcon() {
      switch(this.data.status) {
        case 'in_progress': return({icon:'mdi-clock-time-four', color:'defaultIconColor'})
        case 'success': return({icon:'mdi-check-circle', color:'success'})
        case 'done': return({icon:'mdi-check-circle', color:'success'})
        case 'error': return({icon:'mdi-alert-circle', color:'error'})
        case 'partial_error': return({icon:'mdi-alert-octagon', color:'warning'})
        default: return({icon:'mdi-information', color:'defaultIconColor'})
      }
    },

    showTextMessage() {
      return this.data.status == 'error' && this.data.text
    },

    copyableDownloadUrl() {
      // A URL may legitimately be present in callbacks for uploads and other
      // operations. Only the multi-node ZIP action exposes a URL the user can
      // download or share from the background-actions tray.
      if (!['share-download', 'share_download'].includes(String(this.data.type).toLowerCase())) {
        return null
      }

      return this.findDownloadUrl(this.data)
    },

  },

  methods: {
    ...mapActions('background_actions', [
      BackgroundActions.CANCEL_BACKGROUND_ACTION,
    ]),

    cancel() {
      this[BackgroundActions.CANCEL_BACKGROUND_ACTION](this.data)
    },

    async copyDownloadLink() {
      if (!this.copyableDownloadUrl) {
        this.$store.dispatch('app/openAppErrorMessage', this.$t('copy_download_link_error'))
        return
      }

      try {
        await this.copyToClipboard(this.copyableDownloadUrl)
        this.$store.dispatch('app/openAppInfoMessage', this.$t('download_link_copied'))
      } catch (error) {
        this.$store.dispatch('app/openAppErrorMessage', this.$t('copy_download_link_error'))
      }
    },

    async copyToClipboard(value) {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(value)
          return
        } catch (error) {
          // Use the legacy fallback when the browser denies clipboard access.
        }
      }

      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      const copied = document.execCommand('copy')
      document.body.removeChild(textarea)

      if (!copied) {
        throw new Error('Unable to copy download link')
      }
    },

    findDownloadUrl(value) {
      if (typeof value === 'string') {
        let parsedValue = value

        try {
          parsedValue = JSON.parse(value)
        } catch (error) {
          const markdownUrl = value.match(/^\[[\s\S]*\]\((https?:\/\/[^)]+)\)$/)
          const url = markdownUrl ? markdownUrl[1] : value.match(/https?:\/\/[^\s<>"')\]]+/)?.[0]
          return url || null
        }

        return this.findDownloadUrl(parsedValue)
      }

      if (!value || typeof value !== 'object') {
        return null
      }

      const preferredUrl = value.download_url || value.downloadUrl || value.url || value.URL
      if (preferredUrl) {
        return this.findDownloadUrl(preferredUrl)
      }

      return Object.values(value)
        .map(item => this.findDownloadUrl(item))
        .find(Boolean) || null
    }
  }
}

</script>
